import { useState, useEffect } from 'react';
import { AlertCircle, Download, FileText } from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

interface DocumentPreviewProps {
  hasUrl: boolean;
  fileType: string;
  previewUrl: string;
  className?: string;
}

const DocumentPreview = ({ previewUrl, fileType, hasUrl, className = '' }: DocumentPreviewProps) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (previewUrl) {
      setLoading(false);
    }
  }, [previewUrl]);

  const handleDownload = () => {
    if (previewUrl) {
      window.open(previewUrl, '_blank');
    }
  };

  const renderErrorState = () => (
    <Card className={className}>
      <CardContent className="flex flex-col items-center justify-center h-full min-h-[400px] gap-4">
        <AlertCircle className="h-8 w-8 text-red-500" />
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
          <Button
            variant="outline"
            onClick={handleDownload}
          >
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderPreview = () => {
    if (!previewUrl) return null;

    switch (fileType) {
      case 'application/pdf':
        return (
          <div className="w-full h-full min-h-[800px] bg-gray-50">
            <iframe
              src={previewUrl}
              className="w-full h-full min-h-[800px]"
              title="PDF Preview"
              onError={() => setError('Failed to load PDF preview')}
            />
          </div>
        );

      case 'image/jpeg':
      case 'image/png':
      case 'image/gif':
      case 'image/webp':
        return (
          <div className="w-full h-full flex items-center justify-center bg-gray-50">
            <img
              src={previewUrl}
              alt="Document Preview"
              className="max-w-full max-h-full object-contain"
              onError={() => setError('Failed to load image')}
            />
          </div>
        );

      case 'text/plain':
      case 'text/html':
      case 'text/markdown':
        return (
          <iframe
            src={previewUrl}
            className="w-full h-full min-h-[600px]"
            title="Text Preview"
            onError={() => setError('Failed to load text preview')}
          />
        );

      default:
        return (
          <div className="flex flex-col items-center justify-center h-full min-h-[400px] gap-4">
            <FileText className="h-16 w-16 text-gray-400" />
            <p className="text-gray-600">Preview not available for this file type</p>
            <Button variant="outline" onClick={handleDownload}>
              <Download className="h-4 w-4 mr-2" />
              Download File
            </Button>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="flex items-center justify-center h-full min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return renderErrorState();
  }

  return (
    <Card className={`${className} overflow-hidden`}>
      <CardContent className="p-0">
        {renderPreview()}
      </CardContent>
    </Card>
  );
};

export default DocumentPreview;